import React from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import Alert from 'reactstrap/lib/Alert';
import Button from 'reactstrap/lib/Button';
import Card from 'reactstrap/lib/Card';
import CardBody from 'reactstrap/lib/CardBody';
import CardHeader from 'reactstrap/lib/CardHeader';
import Col from 'reactstrap/lib/Col';
import Collapse from 'reactstrap/lib/Collapse';
import FormFeedback from 'reactstrap/lib/FormFeedback';
import FormGroup from 'reactstrap/lib/FormGroup';
import Label from 'reactstrap/lib/Label';
import Row from 'reactstrap/lib/Row';
import Spinner from 'reactstrap/lib/Spinner';

import debounce from 'lodash/debounce';

import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { loadCart, updateCartShippingInformation } from '@ttstr/actions';
import { Cart, CartValidations, ShippingInformation, ShippingMethods, countriesThatRequireState } from '@ttstr/api';
import { AppState } from '@ttstr/reducers';

import { useActions, useShallowEqualSelector } from '@ttstr/utils';

import { ContentrArea } from '@ttstr/components';
import { useAuth } from '../Auth/AuthContext';
import Container from '../Container/Container';
import CustomerDataFields from '../Customer/CustomerDataFields';
import CountrySelect from '../Form/CountrySelect';
import FormField from '../Form/FormField';
import ValidationAlert from '../Form/ValidationAlert';
import LoadingSpinner from '../Loading/LoadingSpinner';
import UserLoginModal from '../Navigation/UserLoginModal';
import CartListing from '../ShoppingCart/CartListing';
import RadioList from '../RadioList/RadioList';
import RadioListItem from '../RadioList/RadioListItem';
import Sticky from '../Sticky/Sticky';

interface OwnProps {
  closeIcon?: React.ReactNode;
}

type Props = Readonly<OwnProps>;

const Shipping: React.FC<Props> = ({ closeIcon }) => {
  const { t } = useTranslation();
  const { cart, cartReceived, loading } = useShallowEqualSelector(mapStateToProps);

  const defaultValues: ShippingInformation = React.useMemo(() => {
    if (!cart) return;
    return { ...cart };
  }, [cart]);

  if ((!cartReceived && loading) || !cart) {
    return <LoadingSpinner label={t(`LOADING.CART`)} />;
  }

  return (
    <ShippingForm
      availableShippingMethods={cart.available_shipping_methods}
      defaultValues={defaultValues}
      closeIcon={closeIcon}
      overrideValidations={cart.validations}
      externalAlertMessage={cart.no_checkout_message}
    />
  );
};

interface ShippingFormProps {
  closeIcon?: React.ReactNode;
  availableShippingMethods: ShippingMethods;
  defaultValues: ShippingInformation;
  overrideValidations?: CartValidations;
  externalAlertMessage?: string;
}

const ShippingFormComponent: React.FC<ShippingFormProps> = ({
  closeIcon,
  availableShippingMethods,
  defaultValues,
  overrideValidations,
  externalAlertMessage,
}) => {
  const { t } = useTranslation();

  const history = useHistory();
  const formRef = React.useRef<HTMLFormElement>();
  const [updating, setUpdating] = React.useState(false);
  const [showLogin, setShowLogin] = React.useState(false);
  const abortController = React.useRef<AbortController>();
  const { loggedIn, loggingIn } = useAuth();
  const { updateCartShippingInformation, loadCart } = useActions(mapDispatchToProps);
  const formContext = useForm<ShippingInformation>({
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues,
  });

  const { control, errors, handleSubmit, formState, getValues, reset, setValue, trigger } = formContext;
  const { isSubmitting, isValid } = formState;

  const shipping_country = String(useWatch({ control, name: 'shipping_country' })).toUpperCase();
  const currentShippingMethodSid = useWatch({ control, name: 'shipping_method_sid' });
  const useShippingAddress = Boolean(useWatch({ control, name: 'use_shipping_address' }));

  React.useEffect(() => {
    const currentMethod = currentShippingMethodSid;
    const defaultMethod = defaultValues.shipping_method_sid;
    if (currentMethod && availableShippingMethods.find((m) => m.sid === currentMethod)) return;
    if (currentMethod === defaultMethod) return;

    setValue('shipping_method_sid', defaultMethod, { shouldValidate: true });
  }, [currentShippingMethodSid, defaultValues.shipping_method_sid, availableShippingMethods, setValue]);

  const isShippingMethodAvailable = React.useMemo(() => {
    return availableShippingMethods.some((s) => s.sid === currentShippingMethodSid && s.sid !== 'online');
  }, [availableShippingMethods, currentShippingMethodSid]);

  const reinitalizeWithCartProps = React.useCallback(async () => {
    try {
      setUpdating(true);
      const newCart = ((await loadCart()) as any) as Cart;
      reset(newCart, { isValid });
    } finally {
      setUpdating(false);
    }
  }, []);

  const onFormChanged = React.useCallback(
    debounce(async (values: Partial<ShippingInformation>) => {
      try {
        if (abortController.current) abortController.current.abort();
        abortController.current = new AbortController();
        setUpdating(true);
        await updateCartShippingInformation(values, abortController.current.signal);
      } catch (e) {
        if (!(e instanceof DOMException && e.name === 'AbortError')) throw e;
      } finally {
        abortController.current = null;
        setUpdating(false);
      }
    }, 100),
    [setUpdating, updateCartShippingInformation]
  );

  function toggleUserLoginModal() {
    setShowLogin(!showLogin);
  }

  const onCountryChanged = React.useCallback(
    (countryCode: string) => {
      onFormChanged({
        country: countryCode,
      });
      trigger('state');
    },
    [onFormChanged]
  );

  const onShippingCountryChanged = React.useCallback(
    (countryCode: string) => {
      onFormChanged({
        shipping_country: countryCode,
      });
      trigger('shipping_state');
    },
    [onFormChanged]
  );

  const onUseShippingAddressChanged = React.useCallback(
    (useShippingAddress: boolean) => {
      onFormChanged({
        use_shipping_address: Boolean(useShippingAddress),
      });
    },
    [onFormChanged]
  );

  const onSubmit = async (values: ShippingInformation) => {
    const currentShippingMethod = availableShippingMethods.find((m) => m.sid === values.shipping_method_sid);
    await updateCartShippingInformation({
      ...values,
      // add shipping method id to the payload
      shipping_method_id: currentShippingMethod?.id,
    });
    history.push('/checkout/payment');
  };

  return (
    <FormProvider {...formContext}>
      <Container className="mb-5">
        <Row>
          <Col lg="6">
            <div className="tt-sticky-top mb-2">
              <legend>{t('CART.TITLE')}</legend>
              <CartListing />
            </div>
          </Col>
          <Col lg="6">
            <form onSubmit={handleSubmit(onSubmit)} id="shipment-form" ref={formRef}>
              <fieldset disabled={isSubmitting}>
                <FormGroup tag="fieldset" className="mb-0">
                  <legend className="legend--recipient">{t(`CHECKOUT.RECIPIENT`)}</legend>
                  <Alert
                    color="info"
                    className="d-flex justify-content-between align-items-center"
                    isOpen={!loggedIn && !loggingIn}
                  >
                    {t('CUSTOMER.ASK_FOR_ACCOUNT')}
                    <Button
                      type="button"
                      color="primary"
                      size="sm"
                      className="text-nowrap"
                      onClick={() => setShowLogin(true)}
                    >
                      {t('CUSTOMER.LOGIN_NOW')}
                    </Button>
                  </Alert>

                  <CustomerDataFields
                    mode="create"
                    onCountryChanged={onCountryChanged}
                    overrideValidations={overrideValidations}
                  />
                </FormGroup>
                <FormGroup tag="fieldset" className="mb-0">
                  <legend className="legend--shipping-method">{t(`CHECKOUT.SHIPPING_METHOD`)}</legend>
                  <Label>{t(`CHECKOUT.SHIPPING_METHOD_DESCRIPTION`)}</Label>

                  <FormGroup tag="fieldset" className="mt-2" disabled={updating}>
                    {Boolean(availableShippingMethods.length) ? (
                      <>
                        <RadioList hasErrors={Boolean(errors.shipping_method_sid)} className="shipping-method-list">
                          {availableShippingMethods.map((shippingMethod) => {
                            return (
                              <RadioListItem
                                key={shippingMethod.id}
                                iconUrl={shippingMethod.icon}
                                inputId={`shipping_method_${shippingMethod.sid}`}
                                title={shippingMethod.title}
                                description={shippingMethod.description}
                                name="shipping_method_sid"
                                value={shippingMethod.sid}
                                price={Number(shippingMethod.price)}
                              />
                            );
                          })}
                        </RadioList>
                        {errors.shipping_method_sid && (
                          <FormFeedback>
                            {t(`FORM.VALIDATION.${errors.shipping_method_sid.type.toUpperCase()}`)}
                          </FormFeedback>
                        )}
                      </>
                    ) : (
                      <>
                        <input
                          type="hidden"
                          name="shipping_method_sid"
                          ref={formContext.register({ required: true })}
                        />
                        <Alert color="info">
                          <div dangerouslySetInnerHTML={{ __html: t('CHECKOUT.NO_AVAILABLE_SHIPPING_METHOD') }} />
                        </Alert>
                      </>
                    )}
                    {updating && <LoadingSpinner />}
                  </FormGroup>

                  <Collapse isOpen={isShippingMethodAvailable}>
                    <FormGroup>
                      <Card color="light">
                        <CardHeader>
                          <FormField
                            type="checkbox"
                            label={
                              <Trans i18nKey="CUSTOMER.DIFFERING_SHIPPING_ADDRESS">
                                {/* eslint-disable react/jsx-no-literals */}
                                Ich möchte eine <span className="text-danger">abweichende Lieferadresse</span> angeben
                                {/* eslint-enable react/jsx-no-literals */}
                              </Trans>
                            }
                            name="use_shipping_address"
                            formGroupClassName="mb-0"
                            onChange={(e) => onUseShippingAddressChanged(e.target.checked)}
                            validationOptions={{}}
                          />
                        </CardHeader>
                        <Collapse isOpen={useShippingAddress}>
                          {useShippingAddress && (
                            <CardBody>
                              <FormGroup tag="fieldset" className="mb-0">
                                <p>{t('CUSTOMER.DIFFERING_SHIPPING_ADDRESS_EXPLAIN')}</p>
                                <Row>
                                  <Col>
                                    <FormField
                                      type="text"
                                      label={t('CUSTOMER.FIRSTNAME')}
                                      name="shipping_firstname"
                                      autoComplete="shipping given-name"
                                      validationOptions={{ required: true }}
                                    />
                                  </Col>
                                  <Col>
                                    <FormField
                                      type="text"
                                      label={t('CUSTOMER.LASTNAME')}
                                      name="shipping_lastname"
                                      autoComplete="shipping family-name"
                                      validationOptions={{ required: true }}
                                    />
                                  </Col>
                                </Row>
                                <Row>
                                  <Col>
                                    <FormField
                                      type="text"
                                      label={t('CUSTOMER.COMPANY_NAME')}
                                      name="shipping_companyname"
                                      autoComplete="shipping organization"
                                      optional
                                      validationOptions={{}}
                                    />
                                  </Col>
                                </Row>
                                <Row>
                                  <Col>
                                    <FormField
                                      type="text"
                                      label={t('CUSTOMER.STREET')}
                                      name="shipping_street"
                                      autoComplete="shipping address-line1"
                                      validationOptions={{ required: true }}
                                    />
                                  </Col>
                                </Row>
                                <Row>
                                  <Col xs="4">
                                    <FormField
                                      type="text"
                                      label={t('CUSTOMER.ZIP_CODE')}
                                      name="shipping_zip_code"
                                      autoComplete="shipping postal-code"
                                      validationOptions={{ required: true }}
                                    />
                                  </Col>
                                  <Col>
                                    <FormField
                                      type="text"
                                      label={t('CUSTOMER.CITY')}
                                      name="shipping_city"
                                      autoComplete="shipping address-level2"
                                      validationOptions={{ required: true }}
                                    />
                                  </Col>
                                </Row>
                                <Row>
                                  <Col>
                                    <FormField
                                      type="text"
                                      label={t('CUSTOMER.STATE')}
                                      name="shipping_state"
                                      autoComplete="shipping address-level1"
                                      optional={!countriesThatRequireState.includes(shipping_country)}
                                      validationOptions={{
                                        validate: {
                                          required: (value) => {
                                            if (
                                              !countriesThatRequireState.includes(
                                                String(getValues().shipping_country).toUpperCase()
                                              )
                                            ) {
                                              return true;
                                            }
                                            return !!value.length;
                                          },
                                        },
                                      }}
                                    />
                                  </Col>
                                </Row>
                                <Row>
                                  <Col>
                                    <CountrySelect
                                      name="shipping_country"
                                      autoComplete="shipping country"
                                      onChange={onShippingCountryChanged}
                                      validationOptions={{ required: true }}
                                    />
                                  </Col>
                                </Row>
                                <Row>
                                  <Col>
                                    <FormField
                                      type="text"
                                      label={t('CUSTOMER.ADDRESS_ADDITION')}
                                      name="shipping_address_addition"
                                      helpText={t('CUSTOMER.ADDRESS_ADDITION_HELP')}
                                      optional
                                      autoComplete="shipping address-line3"
                                      validationOptions={{}}
                                    />
                                  </Col>
                                </Row>
                              </FormGroup>
                            </CardBody>
                          )}
                        </Collapse>
                      </Card>
                    </FormGroup>
                  </Collapse>
                </FormGroup>
              </fieldset>

              <ContentrArea id="after-shipping" />

              <Button
                type="submit"
                disabled={isSubmitting || updating || !isValid}
                className="invisible position-absolute"
                id="form-submit"
                tabIndex={-1}
              >
                {t('CHECKOUT.CHOOSE_PAYMENT_METHOD')}
              </Button>
            </form>
          </Col>
        </Row>
      </Container>
      <Sticky bottom when={isValid}>
        <Container className="pt-2">
          <div className="d-flex flex-column flex-md-row justify-content-md-end align-items-md-center">
            <ValidationAlert className="mb-2" formElement={formRef} externalAlertMessage={externalAlertMessage} />
            {/**
             * NOT WORKING IN IE11: https://stackoverflow.com/questions/28762128/form-button-not-working-in-ie11
             * See WORKAROUND PART 1 and 2!
             */}
            {/* <Button
              className="mb-2"
              type="submit"
              size="lg"
              color="primary"
              form="shipment-form"
              disabled={isSubmitting || updating || !isValid}
            >
              <span className="d-flex justify-content-center align-items-center">
                {t('CHECKOUT.CHOOSE_PAYMENT_METHOD')}
                <Spinner color="light" size="sm" className={isSubmitting ? 'ml-2 d-inline-block' : 'd-none'} />
              </span>
            </Button> */}
            {/* WORKAROUND PART 1: */}
            <Button
              tag="label"
              htmlFor="form-submit"
              className="mb-2"
              size="lg"
              color="primary"
              disabled={isSubmitting || updating || !isValid || !!externalAlertMessage}
              tabIndex={0}
            >
              <span className="d-flex justify-content-center align-items-center">
                {t('CHECKOUT.CHOOSE_PAYMENT_METHOD')}
                <Spinner color="light" size="sm" className={isSubmitting ? 'ml-2 d-inline-block' : 'd-none'} />
              </span>
            </Button>
          </div>
        </Container>
      </Sticky>
      <UserLoginModal
        show={showLogin}
        toggle={toggleUserLoginModal}
        closeIcon={closeIcon}
        onLoginSuccess={reinitalizeWithCartProps}
      >
        <div className="alert alert-default p-0">{t(`LOGIN.INTRO`)}</div>
      </UserLoginModal>
    </FormProvider>
  );
};

const ShippingForm = React.memo(ShippingFormComponent);

const mapStateToProps = (state: AppState) => {
  const { cart, cartReceived, loading } = state.Cart;
  return {
    cart,
    cartReceived,
    loading,
  };
};

const mapDispatchToProps = {
  updateCartShippingInformation,
  loadCart,
};

export default React.memo(Shipping);
